import type { ReactNode } from "react";
import {
  ArticleIcon,
  BuildingsIcon,
  ChatCircleTextIcon,
  ChartLineUpIcon,
  ChecksIcon,
  DownloadSimpleIcon,
  GaugeIcon,
  GearSixIcon,
  GlobeHemisphereWestIcon,
  HouseLineIcon,
  ShareNetworkIcon,
  SparkleIcon,
  TrayIcon,
  TranslateIcon,
  UsersThreeIcon,
} from "@phosphor-icons/react";
import type { Permission } from "@bres/domain";
import type { Hue } from "@bres/ui";

export interface NavItem {
  to: string;
  labelKey: string;
  icon: ReactNode;
  /** Gebiedstint van de icoontegel (navigatie en paginakop). */
  hue: Hue;
  /** Tenantrecht; platformitems hebben geen tenantrecht (alleen platformbeheerders zien ze). */
  permission?: Permission;
  /** Sleutel in /v1/overview counts voor een teller. */
  countKey?: string;
  quietCount?: boolean;
  /** Sneltoets na "g" (bijvoorbeeld g w voor woningen). */
  shortcut?: string;
}

export interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

/** Navigatie binnen één klant (tenant). */
export const NAV: NavGroup[] = [
  { labelKey: "nav.group_today", items: [{ to: "/", labelKey: "nav.overview", icon: <HouseLineIcon />, hue: "sun", permission: "property.read", shortcut: "o" }] },
  {
    labelKey: "nav.group_clients",
    items: [
      { to: "/leads", labelKey: "nav.leads", icon: <TrayIcon />, hue: "rose", permission: "crm.read", countKey: "new_leads", shortcut: "a" },
      { to: "/contacts", labelKey: "nav.contacts", icon: <UsersThreeIcon />, hue: "rose", permission: "crm.read", shortcut: "c" },
      { to: "/tasks", labelKey: "nav.tasks", icon: <ChecksIcon />, hue: "rose", permission: "crm.read", countKey: "my_overdue", shortcut: "t" },
    ],
  },
  {
    labelKey: "nav.group_listings",
    items: [
      { to: "/properties", labelKey: "nav.properties", icon: <BuildingsIcon />, hue: "teal", permission: "property.read", shortcut: "w" },
      { to: "/localization", labelKey: "nav.localization", icon: <TranslateIcon />, hue: "teal", permission: "property.read", countKey: "my_reviews", quietCount: true, shortcut: "l" },
    ],
  },
  {
    labelKey: "nav.group_distribution",
    items: [
      { to: "/website", labelKey: "nav.website", icon: <GlobeHemisphereWestIcon />, hue: "blue", permission: "property.read", shortcut: "s" },
      { to: "/blog", labelKey: "nav.blog", icon: <ArticleIcon />, hue: "gold", permission: "property.read", shortcut: "b" },
      { to: "/chat", labelKey: "nav.chat", icon: <ChatCircleTextIcon />, hue: "gold", permission: "crm.read" },
      { to: "/channels", labelKey: "nav.channels", icon: <ShareNetworkIcon />, hue: "blue", permission: "property.read", countKey: "channel_rejections", shortcut: "p" },
      { to: "/imports", labelKey: "nav.imports", icon: <DownloadSimpleIcon />, hue: "blue", permission: "property.read", countKey: "imports_waiting", quietCount: true, shortcut: "i" },
    ],
  },
  {
    labelKey: "nav.group_system",
    items: [
      { to: "/ai", labelKey: "nav.ai", icon: <SparkleIcon />, hue: "gold", permission: "property.read" },
      { to: "/settings", labelKey: "nav.settings", icon: <GearSixIcon />, hue: "slate", permission: "property.read", shortcut: "e" },
    ],
  },
];

/**
 * Navigatie van het platform (BRES zelf, ADR 0007). AI-opties per klant en de BRES-website (blog, chatbot) staan
 * hier als eigen onderdelen (ADR 0008).
 */
export const PLATFORM_NAV: NavGroup[] = [
  {
    labelKey: "nav.group_platform",
    items: [
      { to: "/platform", labelKey: "nav.platform_dashboard", icon: <ChartLineUpIcon />, hue: "sun", shortcut: "d" },
      { to: "/platform/sites", labelKey: "nav.platform_sites", icon: <GaugeIcon />, hue: "green", shortcut: "q" },
      { to: "/platform/ai", labelKey: "nav.platform_ai", icon: <SparkleIcon />, hue: "gold" },
    ],
  },
  {
    labelKey: "nav.group_bres_site",
    items: [
      { to: "/platform/bres/blog", labelKey: "nav.platform_bres_blog", icon: <ArticleIcon />, hue: "blue" },
      { to: "/platform/bres/chat", labelKey: "nav.platform_bres_chat", icon: <ChatCircleTextIcon />, hue: "blue" },
    ],
  },
];

export const isPlatformPath = (path: string) => path === "/platform" || path.startsWith("/platform/");

/** Het navigatie-item bij een pad (langste overeenkomst), voor de tegel in de paginakop. */
export function navItemFor(path: string): NavItem | undefined {
  const items = [...NAV, ...PLATFORM_NAV].flatMap((g) => g.items);
  return items
    .filter((i) => (i.to === "/" ? path === "/" : path === i.to || path.startsWith(`${i.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0];
}
